import React, { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Label } from "../ui/Label";
import { SuccessMessage } from "../ui/SuccessMessage";
import { ErrorMessage } from "../ui/ErrorMessage";
import { User } from "../../domain/types/auth.types";
import { ProfileService } from "../../services/profile.service";

const profileSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  companyRole: z.string().optional(),
});

type ProfileSchemaType = z.infer<typeof profileSchema>;

interface ProfileInfoProps {
  user: User;
  onUpdate: (user: User) => void;
}

export const ProfileInfo: React.FC<ProfileInfoProps> = ({ user, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Store original values to track changes
  const originalValues = useMemo(
    () => ({
      firstName: user.firstName,
      lastName: user.lastName,
      companyRole: user.companyRole || "",
    }),
    [user.firstName, user.lastName, user.companyRole]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<ProfileSchemaType>({
    resolver: zodResolver(profileSchema),
    defaultValues: originalValues,
  });

  // Watch all form fields to detect changes
  const formValues = watch();

  // Determine if there are actual changes compared to original values
  const hasChanges = useMemo(() => {
    const currentFirstName = formValues.firstName || "";
    const currentLastName = formValues.lastName || "";
    const currentCompanyRole = formValues.companyRole || "";

    return (
      currentFirstName !== originalValues.firstName ||
      currentLastName !== originalValues.lastName ||
      currentCompanyRole !== originalValues.companyRole
    );
  }, [formValues, originalValues]);

  const onSubmit = async (data: ProfileSchemaType) => {
    setIsLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      // Build payload with only changed fields
      const changedFields: {
        firstName?: string;
        lastName?: string;
        companyRole?: string;
      } = {};

      const currentFirstName = data.firstName || "";
      const currentLastName = data.lastName || "";
      const currentCompanyRole = data.companyRole || "";

      // Add field to payload if it changed from original value
      if (currentFirstName !== originalValues.firstName) {
        changedFields.firstName = data.firstName;
      }
      if (currentLastName !== originalValues.lastName) {
        changedFields.lastName = data.lastName;
      }
      if (currentCompanyRole !== originalValues.companyRole) {
        // If field was filled but now empty, send empty string
        // If field value changed, send new value
        changedFields.companyRole = currentCompanyRole;
      }

      const updatedUser = await ProfileService.updateUserProfile(user.id, changedFields);

      onUpdate(updatedUser);
      setIsEditing(false);
      setSuccessMessage("Profile updated successfully!");
    } catch (error) {
      console.error("Failed to update profile:", error);
      setErrorMessage("Failed to update profile. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    reset(originalValues);
    setIsEditing(false);
  };

  return (
    <div className="bg-white border border-border-gray rounded-none p-6 md:w-4/6">
      <div className="mb-6">
        <h3 className="text-base font-bold uppercase tracking-wide">PERSONAL INFORMATION</h3>
        <p className="text-sm text-gray-600 mt-2">Update your Personal Details</p>
      </div>

      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage("")}
          className="mb-6"
        />
      )}

      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onDismiss={() => setErrorMessage("")}
          className="mb-6"
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-0 max-w-2xl">
        {/* First Name and Last Name Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="relative pb-5">
            <Label htmlFor="firstName" className="block text-sm font-medium text-black mb-2">
              First Name
            </Label>
            <Input
              id="firstName"
              {...register("firstName")}
              disabled={!isEditing || isLoading}
              error={!!errors.firstName}
              className="w-full h-12 bg-disabled-bg border-border-gray rounded-none disabled:bg-disabled-bg disabled:cursor-not-allowed placeholder:text-gray-500"
            />
            {errors.firstName && (
              <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                {errors.firstName.message}
              </p>
            )}
          </div>

          <div className="relative pb-5">
            <Label htmlFor="lastName" className="block text-sm font-medium text-black mb-2">
              Last Name
            </Label>
            <Input
              id="lastName"
              {...register("lastName")}
              disabled={!isEditing || isLoading}
              error={!!errors.lastName}
              className="w-full h-12 bg-disabled-bg border-border-gray rounded-none disabled:bg-disabled-bg disabled:cursor-not-allowed placeholder:text-gray-500"
            />
            {errors.lastName && (
              <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                {errors.lastName.message}
              </p>
            )}
          </div>
        </div>

        {/* Company Role Row */}
        <div className="relative pb-5">
          <Label htmlFor="companyRole" className="block text-sm font-medium text-black mb-2">
            Company Role
          </Label>
          <Input
            id="companyRole"
            {...register("companyRole")}
            disabled={!isEditing || isLoading}
            error={!!errors.companyRole}
            className="w-full h-12 bg-disabled-bg border-border-gray rounded-none disabled:bg-disabled-bg disabled:cursor-not-allowed placeholder:text-gray-500"
            placeholder="Enter Your Role"
          />
          {errors.companyRole && (
            <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
              {errors.companyRole.message}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          {!isEditing ? (
            <Button
              type="button"
              onClick={() => setIsEditing(true)}
              className="px-8 h-12 bg-primary-blue hover:bg-primary-blue/90 text-white rounded-none text-sm"
            >
              Edit Profile
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="px-8 h-12 border-border-gray text-black hover:bg-gray-50 rounded-none text-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !hasChanges}
                className="px-8 h-12 bg-primary-blue hover:bg-primary-blue/90 text-white rounded-none text-sm disabled:opacity-50"
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};
